import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import PaginationBar from '../../../../components/PaginationBar/PaginationBar';

import {
   Card,
   CardHead,
   CardHint,
   CardTitle,
   ClickableTr,
   DarkTable,
   EmptyText,
   EmptyTitle,
   EmptyWrap,
   Pill,
   SkeletonBlock,
   TableScroll,
} from '../../../../styles/dunning/base';
import {
   QUEUE_PAGE_SIZE,
   QUEUE_SORTABLE_COLUMNS,
   STAGE_TYPE_PILL_VARIANTS,
   STATUS_SORTABLE_KEYS,
} from '../constants';
import {
   agingInfo,
   caseFigures,
   commitPill,
   needsAssignee,
} from '../utils/derive';
import { formatBRL, formatDoc } from '../../utils/dunningFormat';

// ---------------------------------------------------------------------------
// Visão fila: a MESMA resposta do kanban, sem agrupar. O default é a ordem
// fixa do backend (next_action_at ASC, nulls primeiro = urgência).
//
// Ordenação por cabeçalho é SERVER-SIDE (handoff 0091): o clique sobe pro
// container (`onSortChange`), que refaz o fetch com `order_by`/`order_dir` —
// vale sobre o conjunto completo, não só a página carregada. As colunas
// clicáveis espelham exatamente a whitelist do backend
// (QUEUE_SORTABLE_COLUMNS — navegacao.md §1.10). Ciclo padrão §1.9:
// inativa → asc (▲) → desc (▼) → inativa (volta à urgência).
//
// Paginação é de EXIBIÇÃO (client-side): limita a altura da tabela fatiando a
// resposta já carregada — trocar de página não gera request. A página é
// clampada quando o resultado encolhe (refetch silencioso não reseta).
//
// AS COLUNAS MUDAM POR ABA (handoff 0102): caso encerrado tem `open_*` zerado
// por definição, então "Em aberto"/"Atraso maior" dariam uma tabela de zeros.
// Em Recebidos a coluna de valor vira "Recebido" (ordenável por
// `settled_amount`, §4 do handoff) e o atraso some; em Cancelados vira
// "Cancelado", sem ordenação — o backend não expõe `cancelled_amount` no
// `order_by` e ordenação fantasma é proibida (navegacao.md §1.10).
// ---------------------------------------------------------------------------

// Coluna de valor por aba — o rótulo e a chave de ordenação acompanham o bloco
// de números que o corpo da tabela exibe (`caseFigures`).
const VALUE_COLUMN_BY_STATUS = {
   OPEN: { key: 'open_amount', label: 'Em aberto' },
   SETTLED: { key: 'settled_amount', label: 'Recebido' },
   CANCELLED: { key: 'cancelled_amount', label: 'Cancelado' },
};

// `sortable={false}` → cabeçalho comum, sem ícone e sem onClick. É o item 2 do
// navegacao.md §1.10 aplicado no render: só a coluna que o backend ordena NAQUELE
// recorte vira clicável.
function SortableTh({
   label,
   columnKey,
   numeric = false,
   sortable = true,
   sortConfig,
   onSort,
}) {
   if (!sortable) {
      return <th className={numeric ? 'num' : undefined}>{label}</th>;
   }

   let icon = ' ⇅';
   let ariaSort;
   if (sortConfig.key === columnKey) {
      icon = sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
      ariaSort = sortConfig.direction === 'asc' ? 'ascending' : 'descending';
   }
   return (
      <th
         className={numeric ? 'num' : undefined}
         aria-sort={ariaSort}
         title="Ordenar"
         style={{ cursor: 'pointer', userSelect: 'none' }}
         onClick={() => onSort(columnKey)}
      >
         {label}
         {icon}
      </th>
   );
}

SortableTh.propTypes = {
   label: PropTypes.string.isRequired,
   columnKey: PropTypes.string.isRequired,
   numeric: PropTypes.bool,
   sortable: PropTypes.bool,
   // eslint-disable-next-line react/forbid-prop-types
   sortConfig: PropTypes.object.isRequired,
   onSort: PropTypes.func.isRequired,
};

export default function QueueTable({
   rows,
   today,
   statusFilter,
   loading,
   hasMorePages,
   sortConfig,
   onSortChange,
   onOpenCase,
}) {
   const [page, setPage] = useState(1);

   const isOpenTab = statusFilter === 'OPEN';
   const valueColumn =
      VALUE_COLUMN_BY_STATUS[statusFilter] ?? VALUE_COLUMN_BY_STATUS.OPEN;
   const sortableKeys =
      STATUS_SORTABLE_KEYS[statusFilter] ?? STATUS_SORTABLE_KEYS.OPEN;

   // Ordenação nova reordena o conjunto inteiro no server → volta pra pág. 1.
   useEffect(() => {
      setPage(1);
   }, [sortConfig]);

   const handleSort = (key) => {
      // Gate duplo (§1.10): a chave existe no backend E faz sentido nesta aba.
      if (!QUEUE_SORTABLE_COLUMNS[key] || !sortableKeys.has(key)) return;
      let next;
      if (sortConfig.key === key) {
         next =
            sortConfig.direction === 'asc'
               ? { key, direction: 'desc' }
               : { key: null, direction: null }; // 3º clique → urgência
      } else {
         next = { key, direction: 'asc' };
      }
      onSortChange(next);
   };

   const totalPages = Math.max(1, Math.ceil(rows.length / QUEUE_PAGE_SIZE));
   const safePage = Math.min(page, totalPages);
   const pageRows = rows.slice(
      (safePage - 1) * QUEUE_PAGE_SIZE,
      safePage * QUEUE_PAGE_SIZE
   );

   const countLabel = `${rows.length} ${rows.length === 1 ? 'caso' : 'casos'}`;
   const subtitle = sortConfig.key
      ? `${countLabel} · ordenado por ${QUEUE_SORTABLE_COLUMNS[sortConfig.key]} (${
           sortConfig.direction === 'asc' ? 'crescente' : 'decrescente'
        })`
      : `${countLabel} · ordenado por urgência (compromisso mais antigo primeiro)`;

   return (
      <Card>
         <CardHead>
            <CardTitle>Fila de trabalho</CardTitle>
            {!loading && <CardHint>{subtitle}</CardHint>}
         </CardHead>

         {loading ? (
            <div style={{ padding: 16, display: 'grid', gap: 10 }}>
               {[0, 1, 2, 3, 4, 5].map((i) => (
                  <SkeletonBlock key={i} $h="34px" $radius="8px" />
               ))}
            </div>
         ) : (
            <>
               <TableScroll>
                  <DarkTable>
                     <thead>
                        <tr>
                           <SortableTh
                              label="Cliente"
                              columnKey="customer_name"
                              sortConfig={sortConfig}
                              onSort={handleSort}
                           />
                           <th>Raia</th>
                           <th>Responsável</th>
                           {/* Em caso encerrado a pill não é compromisso: é o
                               desfecho ("Recebido 24/08" / "Cancelado"). */}
                           <th>{isOpenTab ? 'Compromisso' : 'Situação'}</th>
                           <SortableTh
                              label="Tratativas"
                              columnKey="interactions_count"
                              numeric
                              sortable={sortableKeys.has('interactions_count')}
                              sortConfig={sortConfig}
                              onSort={handleSort}
                           />
                           <SortableTh
                              label="Títulos"
                              columnKey="open_count"
                              numeric
                              sortable={sortableKeys.has('open_count')}
                              sortConfig={sortConfig}
                              onSort={handleSort}
                           />
                           <SortableTh
                              label={valueColumn.label}
                              columnKey={valueColumn.key}
                              numeric
                              sortable={sortableKeys.has(valueColumn.key)}
                              sortConfig={sortConfig}
                              onSort={handleSort}
                           />
                           {/* asc = vencimento mais antigo primeiro = MAIS
                               atrasado no topo (semântica do 0091 §2). Só
                               existe no caso aberto. */}
                           {isOpenTab && (
                              <SortableTh
                                 label="Atraso maior"
                                 columnKey="oldest_due_date"
                                 numeric
                                 sortable={sortableKeys.has('oldest_due_date')}
                                 sortConfig={sortConfig}
                                 onSort={handleSort}
                              />
                           )}
                        </tr>
                     </thead>
                     <tbody>
                        {pageRows.map((row) => {
                           const aging = agingInfo(row.oldest_due_date, today);
                           const commit = commitPill(row, today);
                           const figures = caseFigures(row);
                           return (
                              <ClickableTr
                                 key={row.id}
                                 onClick={() => onOpenCase(row.id)}
                              >
                                 <td>
                                    <div
                                       style={{
                                          fontWeight: 700,
                                          fontSize: 12,
                                       }}
                                    >
                                       {row.customer_name}
                                    </div>
                                    <div
                                       style={{
                                          fontSize: 11,
                                          color: '#8a8a8a',
                                          fontVariantNumeric: 'tabular-nums',
                                       }}
                                    >
                                       {formatDoc(row.customer_doc)}
                                    </div>
                                 </td>
                                 <td>
                                    <Pill
                                       $variant={
                                          STAGE_TYPE_PILL_VARIANTS[
                                             row.stage?.stage_type
                                          ] ?? 'neutral'
                                       }
                                    >
                                       {row.stage?.name ?? '—'}
                                    </Pill>
                                 </td>
                                 <td style={{ color: '#444' }}>
                                    {/* Caso do escritório sem responsável: o
                                        escritório não o enxerga até alguém ser
                                        atribuído (0103 §9). Um "—" aqui não
                                        diria que há trabalho parado. */}
                                    {needsAssignee(row) ? (
                                       <Pill
                                          $variant="alert"
                                          title="O escritório só enxerga casos atribuídos a ele"
                                       >
                                          aguardando atribuição
                                       </Pill>
                                    ) : (
                                       row.assigned_user?.nome ?? '—'
                                    )}
                                 </td>
                                 <td>
                                    <Pill $variant={commit.variant}>
                                       {commit.label}
                                    </Pill>
                                 </td>
                                 <td
                                    className="num"
                                    style={{
                                       color: '#444',
                                       fontVariantNumeric: 'tabular-nums',
                                    }}
                                 >
                                    {row.interactions_count || '—'}
                                 </td>
                                 <td
                                    className="num"
                                    style={{
                                       color: '#444',
                                       fontVariantNumeric: 'tabular-nums',
                                    }}
                                 >
                                    {figures.count || '—'}
                                 </td>
                                 <td
                                    className="num"
                                    style={{
                                       fontWeight: 700,
                                       fontVariantNumeric: 'tabular-nums',
                                    }}
                                 >
                                    {formatBRL(figures.amount)}
                                 </td>
                                 {isOpenTab && (
                                    <td className="num">
                                       <Pill $variant={aging.variant}>
                                          {aging.days === null
                                             ? '—'
                                             : `${aging.days} dias`}
                                       </Pill>
                                    </td>
                                 )}
                              </ClickableTr>
                           );
                        })}
                     </tbody>
                  </DarkTable>
               </TableScroll>

               {rows.length === 0 && (
                  <EmptyWrap>
                     <EmptyTitle>Nenhum caso encontrado</EmptyTitle>
                     <EmptyText>Tudo em dia por aqui.</EmptyText>
                  </EmptyWrap>
               )}

               {totalPages > 1 && (
                  <div
                     style={{
                        padding: '4px 16px 12px',
                        borderTop: '1px solid rgba(0, 0, 0, 0.06)',
                     }}
                  >
                     <PaginationBar
                        currentPage={safePage}
                        totalPages={totalPages}
                        onPageChange={setPage}
                     />
                  </div>
               )}

               {hasMorePages && rows.length > 0 && (
                  <EmptyWrap style={{ padding: '12px 16px' }}>
                     <EmptyText>
                        Há mais casos além dos exibidos — refine a busca ou os
                        filtros.
                     </EmptyText>
                  </EmptyWrap>
               )}
            </>
         )}
      </Card>
   );
}

QueueTable.propTypes = {
   // eslint-disable-next-line react/forbid-prop-types
   rows: PropTypes.array.isRequired,
   today: PropTypes.string.isRequired,
   statusFilter: PropTypes.string.isRequired,
   loading: PropTypes.bool.isRequired,
   hasMorePages: PropTypes.bool.isRequired,
   // eslint-disable-next-line react/forbid-prop-types
   sortConfig: PropTypes.object.isRequired,
   onSortChange: PropTypes.func.isRequired,
   onOpenCase: PropTypes.func.isRequired,
};
